/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useState, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronRight,
  ChevronDown,
  Circle,
  CheckCircle2,
  Clock,
  Send,
  PartyPopper,
  Search,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import GoalsConstants from '../../../../constants/goals.constants';
import reviewConstants from '../../../../constants/review.constants';
import UserContext from '../../../../hooks/context/UserContext';
import { cn } from '../../../../lib/utils';

const filterTabs = [
  { id: 'all', label: 'All' },
  { id: 'hard', label: 'Hard' },
  { id: 'soft', label: 'Soft' },
  { id: 'personal', label: 'Personal' },
];

// Minimalistic confetti component
function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const colors = ['#7553EA', '#F59E0B', '#10B981', '#EC4899', '#6366F1'];
    const newParticles = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      left: `${Math.random() * 100}%`,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
      size: 4 + Math.random() * 4,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute animate-confetti"
          style={{
            left: p.left,
            top: '-8px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.size > 6 ? '50%' : '2px',
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function SkillTreeNode({
  node,
  skillsData,
  selectedSkill,
  onSkillSelect,
  expanded,
  onToggle,
  depth = 0,
  isDisabled,
  reviewData,
}) {
  const { socketInstance } = useContext(UserContext);

  // Get children nodes
  const nodeChildren = useMemo(() => {
    if (!node.children?.length) {
      return [];
    }
    return skillsData.filter((item) => node.children?.includes(item.itemId));
  }, [node, skillsData]);

  const hasChildren = nodeChildren.length > 0;
  const isExpanded = expanded.includes(node.itemId);
  const isSelected = selectedSkill?.itemId === node.itemId;
  const isLeafNode = !hasChildren && node.details?.length > 0;

  const isOnHold = node.status === GoalsConstants.GOAL_STATUSES.ON_HOLD;
  const isInProgress = node.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS;
  const isDone = node.status === GoalsConstants.GOAL_STATUSES.DONE;
  const isApproved = node.status === GoalsConstants.GOAL_STATUSES.APPROVED;
  const isDeclined = node.status === GoalsConstants.GOAL_STATUSES.DECLINED;
  const isReviewProcessed = reviewData?.status === reviewConstants.REVIEW_STATUSES.PROCESSED;

  // Check if all lessons are done or approved
  const allLessonsDone = useMemo(() => {
    if (!node.details?.length) return false;
    return node.details.every(
      (detail) =>
        detail.status === GoalsConstants.GOAL_STATUSES.DONE ||
        detail.status === GoalsConstants.GOAL_STATUSES.APPROVED,
    );
  }, [node.details]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggle(node.itemId);
    }
    if (isLeafNode) {
      onSkillSelect(node);
    }
  };

  const handleToggleStatus = (e) => {
    e.stopPropagation();
    if (isDisabled || !isLeafNode) return;

    // Toggle between ON_HOLD and IN_PROGRESS
    const newStatus =
      isOnHold || isDeclined
        ? GoalsConstants.GOAL_STATUSES.IN_PROGRESS
        : GoalsConstants.GOAL_STATUSES.ON_HOLD;

    // Update all nested details (lessons) to match the new status
    if (node.details?.length > 0) {
      node.details.forEach((detail) => {
        // Skip APPROVED details - they shouldn't be changed
        if (detail.status === GoalsConstants.GOAL_STATUSES.APPROVED) {
          return;
        }

        // When checking: set all non-approved lessons to IN_PROGRESS
        // When unchecking: set all non-approved lessons to ON_HOLD
        if (
          (newStatus === GoalsConstants.GOAL_STATUSES.IN_PROGRESS &&
            detail.status !== GoalsConstants.GOAL_STATUSES.IN_PROGRESS) ||
          (newStatus === GoalsConstants.GOAL_STATUSES.ON_HOLD &&
            detail.status !== GoalsConstants.GOAL_STATUSES.ON_HOLD)
        ) {
          socketInstance?.emit('send-nested-goal-status', {
            parentId: node.itemId,
            itemId: detail.itemId,
            status: newStatus,
          });
        }
      });
    }

    socketInstance?.emit('send-goal-status', {
      itemId: node.itemId,
      status: newStatus,
      skillType: node.skillType,
    });
  };

  const getStatusIcon = () => {
    if (hasChildren) return null;

    // APPROVED - show green check, not clickable
    if (isApproved) {
      return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" title="Approved" />;
    }

    // Review requested - show search/review icon for completed skills
    if (isDisabled && (isDone || allLessonsDone)) {
      return (
        <Search className="h-4 w-4 text-primary shrink-0 animate-pulse" title="Under review" />
      );
    }

    // DONE or all lessons done - show check circle, not clickable (waiting for review)
    if (isDone || allLessonsDone) {
      return (
        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" title="All lessons completed" />
      );
    }

    // Review requested - show search icon for in-progress skills
    if (isDisabled && isInProgress) {
      return (
        <Search className="h-4 w-4 text-primary shrink-0 animate-pulse" title="Under review" />
      );
    }

    // ON_HOLD or DECLINED - show empty circle, clickable to add to progress
    if (isOnHold || isDeclined) {
      return (
        <Circle
          className="h-4 w-4 text-gray-300 shrink-0 cursor-pointer hover:text-primary transition-colors"
          onClick={handleToggleStatus}
          title="Add to progress"
        />
      );
    }

    // IN_PROGRESS - show clock icon, clickable to remove from progress
    if (isInProgress) {
      return (
        <Clock
          className="h-4 w-4 text-amber-500 shrink-0 cursor-pointer hover:text-amber-600 transition-colors"
          onClick={handleToggleStatus}
          title="Remove from progress"
        />
      );
    }

    return <Circle className="h-4 w-4 text-gray-300 shrink-0" />;
  };

  // Count children in category nodes
  const childCount = hasChildren ? nodeChildren.length : null;

  return (
    <div className="select-none">
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-2 py-2 px-3 text-left rounded-lg transition-colors text-gray-700',
          'hover:bg-gray-100',
          isSelected && 'bg-primary/10 text-primary',
          depth === 0 && 'font-medium',
        )}
        style={{ paddingLeft: `${depth * 20 + 12}px` }}
        onClick={handleClick}
      >
        {hasChildren ? (
          <span className="shrink-0 w-4 h-4 flex items-center justify-center text-gray-400">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
        ) : (
          getStatusIcon()
        )}
        <span className={cn('flex-1 text-sm truncate', !hasChildren && !isLeafNode && 'ml-4')}>
          {node.name}
        </span>
        {childCount && <span className="text-xs text-gray-400 shrink-0">{childCount}</span>}
      </button>

      {isExpanded && hasChildren && (
        <div className="relative">
          <div
            className="absolute left-0 top-0 bottom-0 w-px bg-gray-200"
            style={{ marginLeft: `${depth * 20 + 22}px` }}
          />
          {nodeChildren.map((child) => (
            <SkillTreeNode
              key={child.itemId}
              node={child}
              skillsData={skillsData}
              selectedSkill={selectedSkill}
              onSkillSelect={onSkillSelect}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
              isDisabled={isDisabled}
              reviewData={reviewData}
            />
          ))}
        </div>
      )}
    </div>
  );
}

SkillTreeNode.propTypes = {
  node: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.array,
    skillType: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  skillsData: PropTypes.array.isRequired,
  selectedSkill: PropTypes.object,
  onSkillSelect: PropTypes.func.isRequired,
  expanded: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  depth: PropTypes.number,
  isDisabled: PropTypes.bool,
  reviewData: PropTypes.shape({
    status: PropTypes.string,
  }),
};

function SkillTreeSidebar({
  skills,
  skillsInProgressCount,
  selectedSkill,
  onSkillSelect,
  activeFilter,
  onFilterChange,
  isDisabled,
  reviewData,
  snackBarMessage,
  onRequestReview,
  isLoadingBtn,
}) {
  // Get root nodes (nodes that are not children of any other node)
  const rootNodes = useMemo(() => {
    return skills.filter((item) => !skills.some((skill) => skill.children?.includes(item.itemId)));
  }, [skills]);

  // Get parent nodes (nodes that have children) - these should be expanded by default
  const parentNodeIds = useMemo(() => {
    return skills.filter((skill) => skill.children?.length > 0).map((s) => s.itemId);
  }, [skills]);

  // Expanded state - initialized with parent nodes expanded
  const [expanded, setExpanded] = useState(parentNodeIds);

  // Update expanded state when filter changes
  React.useEffect(() => {
    setExpanded(parentNodeIds);
  }, [parentNodeIds]);

  const handleToggle = (itemId) => {
    setExpanded((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  };

  const showReviewButton = snackBarMessage === 'Send review?';
  const isReviewRequested = snackBarMessage?.includes('successfully');

  return (
    <Card className="w-full flex flex-col h-full overflow-hidden bg-white border-gray-200">
      <CardHeader className="pb-4 shrink-0 space-y-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-gray-900">Skill Tree</CardTitle>
          <span className="text-sm text-gray-500">{skillsInProgressCount} blocks in progress</span>
        </div>

        {/* Review Banner */}
        {snackBarMessage && (
          <div className="relative flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 overflow-hidden">
            {showReviewButton && <Confetti />}
            <PartyPopper className="h-4 w-4 text-primary shrink-0" />
            <p
              className={cn(
                'text-xs text-gray-700 flex-1',
                (showReviewButton || isReviewRequested) && 'font-semibold',
              )}
            >
              {snackBarMessage}
            </p>
            {showReviewButton && (
              <Button size="sm" onClick={onRequestReview} disabled={isLoadingBtn} className="h-7">
                <Send className="h-3 w-3 mr-1" />
                Send
              </Button>
            )}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={cn(
                'flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                activeFilter === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900',
              )}
              onClick={() => onFilterChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full px-4 pb-4">
          {rootNodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-gray-100 p-4 mb-4">
                <Circle className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">No skills available for this filter.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {rootNodes.map((node) => (
                <SkillTreeNode
                  key={node.itemId}
                  node={node}
                  skillsData={skills}
                  selectedSkill={selectedSkill}
                  onSkillSelect={onSkillSelect}
                  expanded={expanded}
                  onToggle={handleToggle}
                  isDisabled={isDisabled}
                  reviewData={reviewData}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

SkillTreeSidebar.propTypes = {
  skills: PropTypes.array,
  skillsInProgressCount: PropTypes.number,
  selectedSkill: PropTypes.object,
  onSkillSelect: PropTypes.func.isRequired,
  activeFilter: PropTypes.oneOf(['all', 'hard', 'soft', 'personal']),
  onFilterChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  reviewData: PropTypes.shape({
    status: PropTypes.string,
  }),
  snackBarMessage: PropTypes.string,
  onRequestReview: PropTypes.func.isRequired,
  isLoadingBtn: PropTypes.bool,
};

SkillTreeSidebar.defaultProps = {
  skills: [],
  skillsInProgressCount: 0,
  selectedSkill: null,
  activeFilter: 'all',
  isDisabled: false,
  reviewData: null,
  snackBarMessage: null,
  isLoadingBtn: false,
};

export default SkillTreeSidebar;
